import formFillImage from "@assets/images/formFill.png";
import { useUpdateOnboardingUser } from "@features/onboarding/mutations/useUpdateOnboardingUser";
import { zodResolver } from "@hookform/resolvers/zod";
import logger from "@logger";
import Container from "@shared/components/Container";
import InputField from "@shared/components/InputField";
import { useCurrentUser } from "@shared/queries";
import clsx from "clsx";
import { useForm } from "react-hook-form";
import { z } from "zod";

// Validation schema for display name
const displayNameSchema = z.object({
  display_name: z
    .string()
    .trim()
    .min(1, "Display name is required")
    .min(2, "Display name must be at least 2 characters")
    .max(50, "Display name must be less than 50 characters"),
  // .regex(/^[a-zA-Z\s'-]+$/, "Display name can only contain letters, spaces, hyphens, and apostrophes"),
});

type DisplayNameFormData = z.infer<typeof displayNameSchema>;

/**
 * Display name form component for onboarding flow.
 *
 * Displays the formfill image and a form for entering the user's display name
 * with proper validation using React Hook Form and Zod. Handles form submission
 * and navigation automatically using the onboarding mutation.
 *
 * @example
 * ```tsx
 * <DisplayNameForm />
 * ```
 */
interface DisplayNameFormProps {
  onSaved?: () => void;
}

export default function DisplayNameForm({ onSaved }: DisplayNameFormProps) {
  const { user } = useCurrentUser();
  const { mutate: updateOnboardingUser, isPending: isSubmitting, error: mutationError } = useUpdateOnboardingUser();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<DisplayNameFormData>({
    resolver: zodResolver(displayNameSchema),
    defaultValues: {
      display_name: user?.display_name ?? "",
    },
    mode: "onChange",
  });

  const handleFormSubmit = (data: DisplayNameFormData) => {
    logger.info("📝 DisplayNameForm submitting data:", data);
    updateOnboardingUser(data, {
      onSuccess: (result) => {
        logger.info("✅ DisplayNameForm mutation successful:", result);
        onSaved?.();
      },
      onError: (error) => {
        logger.error("❌ DisplayNameForm mutation failed:", error);
      },
    });
  };

  return (
    <div data-testid="display-name-form-root" className="h-full min-h-0 flex flex-col text-center font-mono">
      {/* Content */}
      <div data-testid="display-name-form-content" className="flex-1 overflow-y-auto space-y-4 pb-2">
        {/* Image */}
        <div data-testid="display-name-form-mascot" className="flex justify-center">
          <img
            data-testid="display-name-form-mascot-image"
            src={formFillImage}
            alt="Form fill illustration"
            className="w-36 h-36 object-contain"
          />
        </div>

        {/* Title */}
        <h1 data-testid="display-name-form-heading" className="text-2xl font-bold">
          What's your name?
        </h1>

        {/* Description */}
        <p data-testid="display-name-form-description" className="text-black/80 text-md">
          You won't be able to change it later
        </p>

        {/* Form */}
        <form
          data-testid="display-name-form"
          onSubmit={handleSubmit(handleFormSubmit)}
          className="mx-auto w-full space-y-4"
          noValidate
        >
          {/* Server Error Message */}
          {mutationError && (
            <div data-testid="display-name-form-error" className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p data-testid="display-name-form-error-message" className="text-red-600 text-sm">
                {mutationError.message}
              </p>
            </div>
          )}

          <div data-testid="display-name-form-input-section">
            <Container>
              <InputField
                {...register("display_name")}
                type="text"
                error={errors.display_name?.message}
                className="text-lg"
              />
            </Container>
          </div>
        </form>
      </div>

      {/* Button at bottom */}
      <div data-testid="display-name-form-footer" className="sticky bottom-0 z-10 bg-lavender-400 pt-4">
        <button
          type="submit"
          disabled={isSubmitting}
          onClick={handleSubmit(handleFormSubmit)}
          className={clsx("w-full px-8 py-4 text-lg rounded-lg font-medium transition-colors", {
            "bg-pink-600 text-white hover:bg-pink-700": !isSubmitting,
            "bg-gray-300 text-gray-500 cursor-not-allowed": isSubmitting,
          })}
          data-testid="submit-button"
        >
          <span data-testid="display-name-form-submit-label">{isSubmitting ? "Saving..." : "Continue"}</span>
        </button>
      </div>
    </div>
  );
}
